/**
 * Trunnion AI - Execution Timeline
 * Vertical timeline of execution steps.
 */

import { Bot, UserCheck, GitBranch, Zap, CircleStop, Clock } from 'lucide-react';
import { Timeline } from '@/components/ui/Timeline';
import { Badge } from '@/components/ui/Badge';
import { EXECUTION_STATUS_CONFIG } from '@/config/constants';
import type { ExecutionStep } from '@/types';

interface ExecutionTimelineProps {
  steps: ExecutionStep[];
  selectedStepId?: string | null;
  onStepSelect?: (stepId: string) => void;
}

const nodeTypeIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  agent: Bot,
  humanGate: UserCheck,
  conditional: GitBranch,
  trigger: Zap,
  terminal: CircleStop,
};

function getStatusVariant(status: string): 'completed' | 'active' | 'pending' | 'error' {
  switch (status) {
    case 'completed':
      return 'completed';
    case 'running':
    case 'waiting_approval':
      return 'active';
    case 'failed':
    case 'timed_out':
    case 'cancelled':
      return 'error';
    default:
      return 'pending';
  }
}

export function ExecutionTimeline({
  steps,
  selectedStepId,
  onStepSelect,
}: ExecutionTimelineProps) {
  const timelineItems = steps.map((step) => {
    const Icon = nodeTypeIcons[step.nodeType] ?? Clock;
    const statusConfig = EXECUTION_STATUS_CONFIG[step.status];

    return {
      id: step.id,
      title: step.nodeName,
      subtitle: step.agentName
        ? `Agent: ${step.agentName}`
        : step.nodeType,
      timestamp: step.startedAt
        ? new Date(step.startedAt).toLocaleTimeString()
        : undefined,
      status: getStatusVariant(step.status),
      icon: (
        <Icon
          className={`h-3.5 w-3.5 ${
            step.id === selectedStepId
              ? 'text-trunnion-gold-500'
              : 'text-trunnion-slate-400'
          }`}
        />
      ),
      content: (
        <div
          className={`mt-1 rounded-lg border p-2 cursor-pointer transition-colors ${
            step.id === selectedStepId
              ? 'border-trunnion-gold-500/30 bg-trunnion-gold-500/5'
              : 'border-trunnion-slate-700/50 hover:border-trunnion-slate-600'
          }`}
          onClick={() => onStepSelect?.(step.id)}
        >
          <div className="flex items-center gap-2">
            <Badge
              variant={
                step.status === 'completed'
                  ? 'success'
                  : step.status === 'failed'
                    ? 'error'
                    : step.status === 'running'
                      ? 'info'
                      : 'default'
              }
              dot
            >
              {statusConfig?.label ?? step.status}
            </Badge>
            {step.toolCalls.length > 0 && (
              <span className="text-xs text-trunnion-slate-400">
                {step.toolCalls.length} tool call{step.toolCalls.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          {step.error && (
            <p className="mt-1.5 text-xs text-red-400 line-clamp-2">
              {step.error}
            </p>
          )}
        </div>
      ),
    };
  });

  return <Timeline items={timelineItems} />;
}
